<template>
  <div>
    <div
      v-if="showWarning"
      class="box danger m-b-0"
      style="background-color: #494ab9; position: relative;">
      <div class="box-body">
        <i class="fa fa-exclamation-triangle m-r-sm"/>
        Your e-Invoice has been consolidated and is now ready for submission.
        <a href="/franchise/e-invoice/consolidated"><b> Click to Review </b></a>
        <button
          style="position: absolute; top: 10px; right: 15px; background: transparent; border: none; color: white; font-size: 20px; cursor: pointer;"
          @click="closeWarning">
          &times;
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import swal from 'sweetalert2';

export default {
    data() {
        return {
            showWarning: false,
            poller: null,
            activeSubmissionCompletionToken: null,
        };
    },
    mounted() {
        this.checkConsolidateStatus();
        if (this.hasPendingNotifications()) {
            this.startPolling();
        }
    },
    beforeDestroy() {
        this.stopPolling();
    },
    methods: {
        closeWarning() {
            this.showWarning = false;
        },
        canShowConsolidationCompletionPopup() {
            const path = window.location.pathname.replace(/\/+$/, '');
            const allowedPrefixes = [
                '/franchise/e-invoice/taxpayer',
                '/franchise/e-invoice/business',
                '/franchise/e-invoice/outlet',
                '/franchise/e-invoice/invoice',
                '/franchise/e-invoice/consolidated',
            ];

            return allowedPrefixes.some(
                prefix => path === prefix || path.indexOf(`${prefix}/`) === 0,
            );
        },
        hasPendingNotifications() {
            return !!(
                this.getPendingToken() || this.getSubmissionPendingToken()
            );
        },
        getPendingToken() {
            return window.localStorage.getItem(
                'einvoiceConsolidationPendingToken',
            );
        },
        clearPendingToken() {
            window.localStorage.removeItem('einvoiceConsolidationPendingToken');
        },
        getSubmissionPendingToken() {
            return window.localStorage.getItem(
                'einvoiceSubmissionPendingToken',
            );
        },
        clearSubmissionPendingToken() {
            window.localStorage.removeItem('einvoiceSubmissionPendingToken');
        },
        getLastShownToken() {
            return window.localStorage.getItem(
                'einvoiceConsolidationShownToken',
            );
        },
        setLastShownToken(token) {
            if (!token) return;
            window.localStorage.setItem(
                'einvoiceConsolidationShownToken',
                token,
            );
        },
        getLastShownSubmissionToken() {
            return window.localStorage.getItem('einvoiceSubmissionShownToken');
        },
        setLastShownSubmissionToken(token) {
            if (!token) return;
            window.localStorage.setItem('einvoiceSubmissionShownToken', token);
        },
        getSubmissionShownDocuments(token) {
            if (!token) return {};

            try {
                const raw = window.localStorage.getItem(
                    `einvoiceSubmissionShownDocuments:${token}`,
                );

                return raw ? JSON.parse(raw) : {};
            } catch (error) {
                return {};
            }
        },
        setSubmissionShownDocuments(token, documentMap) {
            if (!token) return;

            window.localStorage.setItem(
                `einvoiceSubmissionShownDocuments:${token}`,
                JSON.stringify(documentMap || {}),
            );
        },
        clearSubmissionShownDocuments(token) {
            if (!token) return;

            window.localStorage.removeItem(
                `einvoiceSubmissionShownDocuments:${token}`,
            );
        },
        startPolling() {
            if (this.poller) return;

            this.poller = window.setInterval(() => {
                this.checkConsolidateStatus();
            }, 10000);
        },
        stopPolling() {
            if (!this.poller) return;

            window.clearInterval(this.poller);
            this.poller = null;
        },
        checkConsolidateStatus() {
            axios
                .get(Laroute.route('franchise.vue.checkConsolidateStatus'))
                .then(({ data }) => {
                    this.showWarning = data.not_validated;

                    const notification = data.notification || null;
                    const pendingToken = this.getPendingToken();
                    const submissionNotification =
                        data.submission_notification || null;
                    const submissionPendingToken = this.getSubmissionPendingToken();

                    if (!notification || !notification.token) {
                        if (!pendingToken && !submissionPendingToken) {
                            this.stopPolling();
                        }
                    } else if (notification.status === 'processing') {
                        this.startPolling();
                    } else if (
                        notification.finished &&
                        pendingToken &&
                        notification.token === pendingToken &&
                        this.getLastShownToken() !== notification.token
                    ) {
                        if (!this.canShowConsolidationCompletionPopup()) {
                            this.stopPolling();
                            return;
                        }

                        this.showCompletionPopup(notification);
                        return;
                    } else if (
                        notification.finished &&
                        pendingToken &&
                        notification.token === pendingToken
                    ) {
                        this.clearPendingToken();
                    }

                    if (
                        !submissionNotification ||
                        !submissionNotification.token
                    ) {
                        if (!this.hasPendingNotifications()) {
                            this.stopPolling();
                        }
                        return;
                    }

                    if (
                        submissionPendingToken &&
                        submissionNotification.token === submissionPendingToken
                    ) {
                        if (
                            submissionNotification.finished &&
                            this.getLastShownSubmissionToken() !==
                                submissionNotification.token
                        ) {
                            this.showSubmissionCompletionPopup(
                                submissionNotification,
                                0,
                            );
                            return;
                        }
                    }

                    if (submissionNotification.status === 'processing') {
                        this.startPolling();
                        return;
                    }

                    if (!this.hasPendingNotifications()) {
                        this.stopPolling();
                    }
                })
                .catch(err => console.error(err));
        },
        showCompletionPopup(notification) {
            const type =
                notification.errors > 0 || notification.skipped > 0
                    ? 'warning'
                    : 'success';
            const title =
                type === 'success'
                    ? 'Consolidation Completed'
                    : 'Consolidation Completed With Issues';

            swal({
                title,
                html: this.buildNotificationHtml(notification),
                type,
                confirmButtonColor: '#0CC2AA',
                confirmButtonText: 'Close',
            }).then(() => {
                this.setLastShownToken(notification.token);
                this.clearPendingToken();

                if (!this.hasPendingNotifications()) {
                    this.stopPolling();
                }
            });
        },
        showSubmissionCompletionPopup(notification, newToastCount = 0) {
            if (
                this.activeSubmissionCompletionToken === notification.token ||
                this.getLastShownSubmissionToken() === notification.token
            ) {
                return;
            }

            this.activeSubmissionCompletionToken = notification.token;
            const delay = Math.min(newToastCount, 5) * 300 + 300;

            window.setTimeout(() => {
                swal({
                    title: 'Submission Completed',
                    html: this.buildSubmissionNotificationHtml(notification),
                    type:
                        notification.errors > 0 || notification.skipped > 0
                            ? 'warning'
                            : 'success',
                    confirmButtonColor: '#0CC2AA',
                    confirmButtonText: 'Close',
                }).then(() => {
                    this.setLastShownSubmissionToken(notification.token);
                    this.clearSubmissionPendingToken();
                    this.activeSubmissionCompletionToken = null;

                    if (!this.hasPendingNotifications()) {
                        this.stopPolling();
                    }
                });
            }, delay);
        },
        showSubmissionDocumentToasts(notification) {
            const token = notification.token;
            const shownDocuments = this.getSubmissionShownDocuments(token);
            const documentResults = Array.isArray(notification.document_results)
                ? notification.document_results
                : [];
            const pendingToasts = [];

            documentResults.forEach(documentResult => {
                if (!this.isSubmissionDocumentFinal(documentResult)) {
                    return;
                }

                const documentKey = this.getSubmissionDocumentToastKey(
                    documentResult,
                );

                if (shownDocuments[documentKey]) {
                    return;
                }

                shownDocuments[documentKey] = true;
                pendingToasts.push(documentResult);
            });

            pendingToasts.forEach((documentResult, index) => {
                window.setTimeout(() => {
                    this.showSubmissionDocumentToast(documentResult);
                }, index * 250);
            });

            this.setSubmissionShownDocuments(token, shownDocuments);

            if (notification.finished) {
                window.setTimeout(() => {
                    this.clearSubmissionShownDocuments(token);
                }, 60000);
            }

            return pendingToasts.length;
        },
        buildNotificationHtml(notification) {
            const rows = [
                ['Requested Invoices', notification.requested || 0],
                ['Eligible Invoices', notification.eligible || 0],
                ['Queued for Consolidation', notification.queued || 0],
                ['Successfully Consolidated', notification.successful || 0],
                ['Skipped', notification.skipped || 0],
                ['Errors', notification.errors || 0],
                [
                    'Jobs Completed',
                    `${notification.completed_jobs ||
                        0}/${notification.total_jobs || 0}`,
                ],
            ];

            const summaryHtml = rows
                .map(
                    ([label, value]) =>
                        `<div style="display:flex;justify-content:space-between;margin-bottom:8px;"><strong>${label}</strong><span>${value}</span></div>`,
                )
                .join('');

            const messages = Array.isArray(notification.messages)
                ? notification.messages
                : [];

            const notesHtml = messages.length
                ? `
                    <div style="margin-top:16px;text-align:left;">
                        <strong>Notes</strong>
                        <ul style="margin:8px 0 0 18px;padding:0;">
                            ${messages
                                .map(
                                    message =>
                                        `<li style="margin-bottom:6px;">${this.escapeHtml(
                                            message,
                                        )}</li>`,
                                )
                                .join('')}
                        </ul>
                    </div>
                `
                : '';

            return `
                <div style="text-align:left;">
                    ${summaryHtml}
                    ${notesHtml}
                </div>
            `;
        },
        buildSubmissionNotificationHtml(notification) {
            const errorSources = this.countSubmissionErrorsBySource(
                notification,
            );
            const rows = [
                ['Requested Documents', notification.requested || 0],
                ['Queued for Submission', notification.queued || 0],
                ['Successfully Submitted', notification.successful || 0],
                ['Skipped', notification.skipped || 0],
                ['Errors', notification.errors || 0],
                ['System Errors', errorSources.system],
                ['LHDN/MyInvois Errors', errorSources.lhdn],
                [
                    'Jobs Completed',
                    `${notification.completed_jobs ||
                        0}/${notification.total_jobs || 0}`,
                ],
            ];

            const summaryHtml = rows
                .map(
                    ([label, value]) =>
                        `<div style="display:flex;justify-content:space-between;margin-bottom:8px;"><strong>${label}</strong><span>${value}</span></div>`,
                )
                .join('');

            const messages = Array.isArray(notification.messages)
                ? notification.messages
                : [];

            const notesHtml = messages.length
                ? `
                    <div style="margin-top:16px;text-align:left;">
                        <strong>Notes</strong>
                        <ul style="margin:8px 0 0 18px;padding:0;">
                            ${messages
                                .map(
                                    message =>
                                        `<li style="margin-bottom:6px;">${this.escapeHtml(
                                            message,
                                        )}</li>`,
                                )
                                .join('')}
                        </ul>
                    </div>
                `
                : '';

            return `
                <div style="text-align:left;">
                    ${summaryHtml}
                    ${notesHtml}
                </div>
            `;
        },
        countSubmissionErrorsBySource(notification) {
            const documentResults = Array.isArray(notification.document_results)
                ? notification.document_results
                : [];

            return documentResults.reduce(
                (counts, documentResult) => {
                    const result = String(
                        documentResult.result || '',
                    ).toLowerCase();
                    const source = String(
                        documentResult.error_source || '',
                    ).toLowerCase();

                    if (result !== 'error') return counts;

                    if (source === 'lhdn') {
                        counts.lhdn += 1;
                    } else if (source === 'system') {
                        counts.system += 1;
                    } else {
                        counts.unknown += 1;
                    }

                    return counts;
                },
                { system: 0, lhdn: 0, unknown: 0 },
            );
        },
        isSubmissionDocumentFinal(documentResult) {
            const result = String(documentResult.result || '').toLowerCase();

            return ['successful', 'error', 'skipped'].includes(result);
        },
        getSubmissionDocumentToastKey(documentResult) {
            return [
                documentResult.document_id || '',
                documentResult.status || '',
                documentResult.result || '',
                documentResult.message || '',
                documentResult.error_source || '',
            ].join(':');
        },
        showSubmissionDocumentToast(documentResult) {
            const toastConfig = this.getSubmissionDocumentToastConfig(
                documentResult,
            );
            const errorSourceLabel = this.getSubmissionErrorSourceLabel(
                documentResult.error_source,
            );

            swal({
                toast: true,
                position: 'top-right',
                showConfirmButton: false,
                timer: 5000,
                timerProgressBar: true,
                type: toastConfig.type,
                title: this.escapeHtml(
                    documentResult.document_number ||
                        `Document #${documentResult.document_id || ''}`,
                ),
                html: `
                    <div style="text-align:left;">
                        <div><strong>Status:</strong> ${this.escapeHtml(
                            documentResult.status || 'Unknown',
                        )}</div>
                        ${
                            errorSourceLabel
                                ? `<div style="margin-top:4px;"><strong>Source:</strong> ${this.escapeHtml(
                                      errorSourceLabel,
                                  )}</div>`
                                : ''
                        }
                        <div style="margin-top:4px;">${this.escapeHtml(
                            documentResult.message || toastConfig.message,
                        )}</div>
                    </div>
                `,
            });

            window.dispatchEvent(
                new CustomEvent('einvoice-submission-document-finished', {
                    detail: {
                        document_id: documentResult.document_id,
                        status: documentResult.status,
                        result: documentResult.result,
                    },
                }),
            );
        },
        getSubmissionDocumentToastConfig(documentResult) {
            const status = String(documentResult.status || '').toLowerCase();
            const result = String(documentResult.result || '').toLowerCase();
            const errorSource = String(
                documentResult.error_source || '',
            ).toLowerCase();

            if (status === 'valid') {
                return {
                    type: 'success',
                    message: 'Document has been validated successfully.',
                };
            }

            if (status === 'submitted') {
                return {
                    type: 'info',
                    message: 'Document has been submitted successfully.',
                };
            }

            if (status === 'invalid') {
                return {
                    type: 'warning',
                    message: 'Document was rejected by LHDN/MyInvois.',
                };
            }

            if (errorSource === 'lhdn') {
                return {
                    type: 'warning',
                    message:
                        'LHDN/MyInvois returned an error while processing document.',
                };
            }

            if (status === 'system error' || result === 'error') {
                return {
                    type: 'error',
                    message: 'System error occurred while processing document.',
                };
            }

            return {
                type: 'info',
                message: 'Document status has been updated.',
            };
        },
        getSubmissionErrorSourceLabel(errorSource) {
            const source = String(errorSource || '').toLowerCase();

            if (source === 'lhdn') return 'LHDN/MyInvois API';
            if (source === 'system') return 'System';

            return '';
        },
        escapeHtml(value) {
            return String(value == null ? '' : value)
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;')
                .replace(/'/g, '&#39;');
        },
    },
};
</script>

<style>
.box-aest {
    padding-bottom: -10%;
}

.renewal-header-old {
    position: fixed;
    top: 3.5rem;
    left: 0;
    right: 0;
    margin: inherit;
    z-index: 1020;
}

.renewal-header ~ .app-body {
    padding-top: 7rem;
}
</style>



// WEBPACK FOOTER //
// resources/assets/js/franchise_component/e-invoice/consolidate-announcement.vue